import { useReactFlow } from '@xyflow/react'
import { Copy, Plus, BarChart3, Network } from 'lucide-react'
import React, { useState } from 'react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'

interface AnalysisResults {
  qualityRating: string
  score: number
  scoreExplanation: string
  recommendations: string[]
  assessment: {
    clarity: {
      score: number
      explanation: string
      recommendations: string[]
      dataPoints: any[]
    }
    completeness: {
      score: number
      explanation: string
      recommendations: string[]
      dataPoints: any[]
    }
    relevance: {
      score: number
      explanation: string
      recommendations: string[]
      dataPoints: any[]
    }
    accuracy: {
      score: number
      explanation: string
      recommendations: string[]
      dataPoints: any[]
    }
  }
  comparison: {
    clarity: string
    completeness: string
    relevance: string
    accuracy: string
  }
  dataPoints: any[]
}

interface NodeAnalysisAndRecommendationsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  analysisResults: AnalysisResults
  onCreateRecommendedNodes: (
    dataPoints: any[],
    selectedIndices: number[]
  ) => Promise<void>
  onCopyToContext: (selectedRecommendations: string[]) => void
  currentNodeId: string // <-- Add this prop
  updateNodeData?: (id: string, data: any) => void // Optional, for direct node data update
}

export const NodeAnalysisAndRecommendationsDialog: React.FC<
  NodeAnalysisAndRecommendationsDialogProps
> = ({
  open,
  onOpenChange,
  analysisResults,
  onCopyToContext,
  onCreateRecommendedNodes,
  currentNodeId,
  updateNodeData,
}) => {
  const [selectedDataPoints, setSelectedDataPoints] = useState<number[]>([])
  const [selectedRecommendations, setSelectedRecommendations] = useState<
    number[]
  >([])
  const { addNodes, getNode, setNodes } = useReactFlow()

  if (!analysisResults) {
    return null
  }

  const getQualityColor = (rating: string) => {
    switch (rating.toLowerCase()) {
      case 'excellent':
        return 'bg-green-100 text-green-800 border-green-200'
      case 'good':
        return 'bg-blue-100 text-blue-800 border-blue-200'
      case 'ok':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200'
      case 'bad':
        return 'bg-red-100 text-red-800 border-red-200'
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600'
    if (score >= 60) return 'text-blue-600'
    if (score >= 40) return 'text-yellow-600'
    return 'text-red-600'
  }

  const handleDataPointToggle = (index: number) => {
    setSelectedDataPoints((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    )
  }

  const handleRecommendationToggle = (index: number) => {
    setSelectedRecommendations((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    )
  }

  const handleSelectAllDataPoints = () => {
    if (selectedDataPoints.length === analysisResults.dataPoints.length) {
      setSelectedDataPoints([])
    } else {
      setSelectedDataPoints(analysisResults.dataPoints.map((_, index) => index))
    }
  }

  const handleSelectAllRecommendations = () => {
    if (
      selectedRecommendations.length === analysisResults.recommendations.length
    ) {
      setSelectedRecommendations([])
    } else {
      setSelectedRecommendations(
        analysisResults.recommendations.map((_, index) => index)
      )
    }
  }

  const handleCreateSelectedNodes = async () => {
    console.log(
      '[NodeAnalysisAndRecommendationsDialog] handleCreateSelectedNodes',
      { selectedDataPoints }
    )
    // Just call the callback, let the parent handle node creation and linking
    await onCreateRecommendedNodes(
      analysisResults.dataPoints,
      selectedDataPoints
    )
  }

  const handleCopySelectedToContext = () => {
    const selectedRecs = selectedRecommendations.map(
      (index) => analysisResults.recommendations[index]
    )
    onCopyToContext(selectedRecs)
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-blue-600" />
            Analysis & Recommendations Details
          </AlertDialogTitle>
          <AlertDialogDescription>
            Comprehensive analysis results with actionable insights and
            recommendations
          </AlertDialogDescription>
        </AlertDialogHeader>
        <ScrollArea className="max-h-[60vh] pr-4">
          <div className="space-y-6">
            {/* Overall Quality Score */}
            <div className="space-y-3">
              <h3 className="text-lg font-semibold">Overall Assessment</h3>
              <div className="flex items-center gap-4">
                <Badge
                  variant="outline"
                  className={`text-sm font-medium ${getQualityColor(analysisResults.qualityRating)}`}
                >
                  Quality: {analysisResults.qualityRating.toUpperCase()}
                </Badge>
                <div
                  className={`text-2xl font-bold ${getScoreColor(analysisResults.score)}`}
                >
                  {analysisResults.score}/100
                </div>
              </div>
              <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded">
                {analysisResults.scoreExplanation}
              </p>
            </div>

            <Separator />

            {/* Detailed Assessment by Criteria */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Detailed Assessment</h3>
              {Object.entries(analysisResults.assessment).map(
                ([criterion, data]) => (
                  <div
                    key={criterion}
                    className="border rounded-lg p-4 space-y-3"
                  >
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium capitalize text-base">
                        {criterion}
                      </h4>
                      <div className={`font-bold ${getScoreColor(data.score)}`}>
                        {data.score}/25
                      </div>
                    </div>
                    <p className="text-sm text-gray-600">{data.explanation}</p>
                    {data.recommendations.length > 0 && (
                      <div className="space-y-1">
                        <p className="text-xs font-medium text-gray-500">
                          Specific Recommendations:
                        </p>
                        <ul className="text-xs text-gray-600 space-y-1">
                          {data.recommendations.map((rec, index) => (
                            <li key={index} className="flex items-start gap-2">
                              <span className="text-blue-500 mt-1">•</span>
                              <span>{rec}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )
              )}
            </div>

            <Separator />

            {/* Comparison with Previous Version */}
            {analysisResults.comparison && (
              <>
                <div className="space-y-3">
                  <h3 className="text-lg font-semibold">
                    Comparison with Previous Version
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {Object.entries(analysisResults.comparison).map(
                      ([criterion, comparison]) => (
                        <div key={criterion} className="border rounded p-3">
                          <h4 className="font-medium capitalize text-sm mb-2">
                            {criterion}
                          </h4>
                          <p className="text-xs text-gray-600">{comparison}</p>
                        </div>
                      )
                    )}
                  </div>
                </div>
                <Separator />
              </>
            )}

            {/* Overall Recommendations */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">All Recommendations</h3>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleSelectAllRecommendations}
                  >
                    {selectedRecommendations.length ===
                    analysisResults.recommendations.length
                      ? 'Deselect All'
                      : 'Select All'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCopySelectedToContext}
                    disabled={selectedRecommendations.length === 0}
                    className="flex items-center gap-1"
                  >
                    <Copy className="h-3 w-3" />
                    Copy to Context
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                {analysisResults.recommendations.map(
                  (recommendation, index) => (
                    <div
                      key={index}
                      className="flex items-start gap-3 p-2 border rounded"
                    >
                      <Checkbox
                        checked={selectedRecommendations.includes(index)}
                        onCheckedChange={() =>
                          handleRecommendationToggle(index)
                        }
                      />
                      <span className="text-sm flex-1">{recommendation}</span>
                    </div>
                  )
                )}
              </div>
            </div>

            <Separator />

            {/* Recommended Nodes to Create */}
            {analysisResults.dataPoints &&
              analysisResults.dataPoints.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold">
                      Recommended Nodes to Create
                    </h3>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleSelectAllDataPoints}
                      >
                        {selectedDataPoints.length ===
                        analysisResults.dataPoints.length
                          ? 'Deselect All'
                          : 'Select All'}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleCreateSelectedNodes}
                        disabled={selectedDataPoints.length === 0}
                        className="flex items-center gap-1"
                      >
                        <Plus className="h-3 w-3" />
                        Create Selected ({selectedDataPoints.length})
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-3">
                    {analysisResults.dataPoints.map((dataPoint, index) => (
                      <div
                        key={index}
                        className="border rounded-lg p-4 space-y-3"
                      >
                        <div className="flex items-start gap-3">
                          <Checkbox
                            checked={selectedDataPoints.includes(index)}
                            onCheckedChange={() => handleDataPointToggle(index)}
                          />
                          <div className="flex-1 space-y-2">
                            <div className="flex items-center gap-2">
                              <Badge variant="secondary" className="text-xs">
                                {dataPoint.nodetype || 'Node'}
                              </Badge>
                              <span className="font-medium text-sm">
                                {dataPoint.nodename || 'Unnamed Node'}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600">
                              {dataPoint.explanation}
                            </p>
                            <p className="text-xs text-blue-600 bg-blue-50 p-2 rounded">
                              <strong>Purpose:</strong> {dataPoint.purpose}
                            </p>{' '}
                            {dataPoint.data && (
                              <p className="text-xs text-gray-500 bg-gray-50 p-2 rounded max-h-20 overflow-y-auto">
                                <strong>Content:</strong>{' '}
                                {(() => {
                                  // Handle both string and object formats for backward compatibility
                                  if (typeof dataPoint.data === 'string') {
                                    return (
                                      dataPoint.data.substring(0, 200) +
                                      (dataPoint.data.length > 200 ? '...' : '')
                                    )
                                  } else if (
                                    typeof dataPoint.data === 'object'
                                  ) {
                                    // Display the main content field (contentMarkdown, diagramContent, etc.)
                                    const content =
                                      dataPoint.data.contentMarkdown ||
                                      dataPoint.data.diagramContent ||
                                      dataPoint.data.command ||
                                      JSON.stringify(dataPoint.data, null, 2)
                                    // Ensure content is a string before calling substring
                                    const contentStr = String(
                                      content || 'No content available'
                                    )
                                    return (
                                      contentStr.substring(0, 200) +
                                      (contentStr.length > 200 ? '...' : '')
                                    )
                                  }
                                  return 'No content available'
                                })()}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
          </div>
        </ScrollArea>{' '}
        <AlertDialogFooter>
          <AlertDialogCancel>Close</AlertDialogCancel>
          <AlertDialogAction asChild>
            <Button
              variant="default"
              className="flex items-center gap-2"
              disabled={
                selectedDataPoints.length === 0 &&
                selectedRecommendations.length === 0
              }
              onClick={() => {
                // Apply selected actions
                if (selectedDataPoints.length > 0) {
                  handleCreateSelectedNodes()
                }
                if (selectedRecommendations.length > 0) {
                  handleCopySelectedToContext()
                }
                // Close the dialog after applying actions
                onOpenChange(false)
              }}
            >
              <Network className="h-4 w-4" />
              Apply Selected Actions
            </Button>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
